import { when } from 'mobx'
import type { FirestoreSlide } from '../firestore/Slide/schema'
import { touchSlide } from '../firestore/Slide'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import {
  SlideDeckType,
  SlideType,
  SlideDeckState,
  SlideDeckMaterialType,
  MediaType,
} from '../types'
import { noTryAsync } from './no-try'
import type { FirestoreSlideDeck } from '../firestore/SlideDeck/schema'
import type { FirestoreSlideDeckMaterial } from '../firestore/SlideDeckMaterial/schema'
import type { FirestoreSlideDeckAuthor } from '../firestore/SlideDeckAuthor/schema'
import type { SlideDeckExhibit } from '../models/SlideDeckExhibit'
import type { Media } from '../models/Media'
import type en from '../../../../apps/web/src/i18n/locale/en.json'
import { SlideQuestionType } from '../models/SlideQuestionType'
import { SlideQuestion } from '../models/SlideQuestion'
import { type SlideModel } from '../models/SlideModel'
import type { SlideRubric } from '../models/SlideRubric'
import { checkIsFreeType } from '../sources-of-truth/checkIsFreeType'
import { validateSlideQuestion } from '../sources-of-truth/validators'
import { PRE_MEETING_QUESTION_SLIDE_ID } from '../constants'
import { type SlideDeckRole } from '../models/SlideDeckRole'
import { RoomState } from '../models/RoomState'

export const MIN_PRICE = 10

interface ValidationContext {
  /** The slide deck being validated */
  slideDeck: FirestoreSlideDeck
  /** All slides in the deck */
  slides: SlideModel[]
  /** All materials (attachments) associated with the deck */
  materials: FirestoreSlideDeckMaterial[]
  /** Authors of the slide deck */
  authors: FirestoreSlideDeckAuthor[]
  /** Exhibits (images/figures) used in the deck */
  exhibits: SlideDeckExhibit[]
  /** Unique identifier of the slide deck */
  slideDeckId: string
  /** The id of the first slide if one is present. It is used when we need to recalculate the slide count (by updating the updatedAt of one of the slides) */
  firstSlideId?: string
  /** All the mediaObjects */
  mediaObjects: {
    [k: string]: Media
  }
  /** All rubrics associated with the deck */
  rubrics: SlideRubric[]

  /** All question (interactive elements) associated with the deck */
  questions: SlideQuestion[]

  /** All roles associated with the deck */
  roles: SlideDeckRole[]
}

export type ErrorKey = keyof (typeof en)['admin_details']['errors']

export const WarningKeys = {
  /** if any discussion slide has a solo bot, show a warning when any are missing a solo bot */
  DISCUSSION_SLIDE_MISSING_SOLO_BOT: 'discussion_slide_missing_solo_bot',
  /** if a non-demo slide deck doesn't have an End of Session slide */
  MISSING_END_OF_SESSION_SLIDE: 'missing_end_of_session_slide',
  /** if a rubric-optional experience type has no rubrics attached */
  RUBRIC_MISSING: 'rubric_missing',
}

const RUBRIC_OPTIONAL_TYPES: SlideDeckType[] = [
  SlideDeckType.dialog,
  SlideDeckType.oral_exam,
  SlideDeckType.tutorial,
]

type WarningKeysType = typeof WarningKeys
type WarningKey = WarningKeysType[keyof WarningKeysType]

export interface ValidationError {
  key: ErrorKey
  options?: Record<string, string>
}

export interface AdminSlideDeckPublishValidationWarning {
  key: WarningKey
  options?: Record<string, string>
  description: string
}

/**
 * Validates a slide deck and returns an array of validation errors
 * @param context - All required data for validation
 * @param repository - Firebase repository instance for async operations
 * @returns Array of validation errors, empty if validation passes
 */
export const validateSlideDeck = async (
  context: ValidationContext,
  repository: FirebaseRepository,
  touchSlideTimeout?: number
): Promise<{
  errors: ValidationError[]
  warnings: AdminSlideDeckPublishValidationWarning[]
}> => {
  const isDemo = context.slideDeck.slideDeckType === SlideDeckType.demo

  const errors: ValidationError[] = [
    ...validateGeneralDetails(context),
    ...validateSlides(context),
    ...(isDemo ? [] : validateAuthors(context)), // not for demos
    ...validateExhibits(context),
    ...validateTypeSpecificRules(context),
    ...validateMaterials(context),
    ...validateEndOfSessionSlide(context),
    ...validateRubrics(context),
    ...validateSoloBot(context),
    ...validateRoles(context),
    ...validatePrice(context),
  ]

  const questionErrors = await validateQuestions(repository, context)

  errors.push(...questionErrors)

  // Only run async validation if no other errors
  if (errors.length === 0) {
    const slideCountError = await validateSlideCount({
      repository: repository,
      slideDeckId: context.slideDeckId,
      slides: context.slides,
      slideDeck: context.slideDeck,
      firstSlideId: context.firstSlideId,
      timeout: touchSlideTimeout,
    })

    if (slideCountError) {
      errors.push(slideCountError)
    }
  }

  // add warnings
  const warnings: AdminSlideDeckPublishValidationWarning[] = [
    ...validateSoloBotDiscussionSlides(context),
    ...validateEndOfSessionSlideWarning(context),
    ...validateRubricsWarning(context),
  ]

  return { errors, warnings }
}

// Helper functions for different validation aspects
const validateGeneralDetails = ({
  slideDeck,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []
  const isDemo = slideDeck.slideDeckType === SlideDeckType.demo
  if (!slideDeck.slideDeckName) {
    errors.push({ key: 'experience_must_have_title' })
  }
  if (!slideDeck.slideDeckTeaser) {
    errors.push({ key: 'experience_must_have_subtitle' })
  }
  if (!slideDeck.slideDeckDescription) {
    errors.push({ key: 'experience_must_have_description' })
  }
  if (!slideDeck.slideDeckImageURL) {
    errors.push({ key: 'experience_must_have_slide_deck_logo' })
  }
  if (!slideDeck.slideDeckVersion && slideDeck.slideDeckVersion !== '0.0.0') {
    errors.push({ key: 'experience_must_have_version' })
  }
  if (!slideDeck.slideDeckLearningObjectives.length && !isDemo) {
    errors.push({ key: 'experience_must_have_learning_objective' })
  }
  if (!slideDeck.slideDeckDisciplines.length && !isDemo) {
    errors.push({ key: 'experience_must_have_discipline' })
  }
  if (
    [SlideDeckState.deleted, SlideDeckState.hidden].includes(
      slideDeck.slideDeckState
    )
  ) {
    errors.push({ key: 'experience_slide_deck_deleted_or_hidden' })
  }

  return errors
}

const validateSlides = ({
  mediaObjects,
  slides,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []

  if (slides.length === 0) {
    errors.push({ key: 'experience_must_have_one_slide' })
    return errors
  }

  if (slides[0].data.slideType === SlideType.soloQuiz) {
    errors.push({
      key: 'experience_solo_quiz_as_first_slide',
      options: {
        slideId: slides[0].id,
        slideName: slides[0].data.slideName,
        slideNumber: String(slides[0].data.slideOrder + 1),
      },
    })
  }

  slides.forEach((slide) => {
    errors.push(...validateSlideByType(mediaObjects, slide.id, slide.data))
  })

  return errors
}

const validateSlideByType = (
  mediaObjects: {
    [k: string]: Media
  },
  slideId: string,
  slide: FirestoreSlide
): ValidationError[] => {
  const errors: ValidationError[] = []
  const options = {
    slideId,
    slideName: slide.slideName,
    slideNumber: String(slide.slideOrder + 1),
  }

  if (!slide.slideName) {
    errors.push({ key: 'slide_missing_slide_name', options })
  }

  switch (slide.slideType) {
    case SlideType.exhibitGrid:
      // Potential future validation: ensure that at least one exhibit is present.
      break
    case SlideType.groupQuiz:
    case SlideType.interactivePoll:
    case SlideType.soloQuiz:
    case SlideType.interactiveQuiz:
      // Potential future validation: validate that there is at least one interactive element.
      break
    case SlideType.sessionResults:
      // Potential future validation: must come after processing data.
      break
    case SlideType.processingData:
      // Potential future validation: must come before session results.
      break
    case SlideType.professorFeedback:
    case SlideType.discussion:
      if (slide.slideImageURL && !slide.slideImageAltText) {
        errors.push({
          key: 'slide_missing_image_alt_text',
          options,
        })
      }
      break
    case SlideType.image:
      if (!slide.slideImageURL) {
        errors.push({ key: 'slide_missing_image_url', options })
      }
      if (!slide.slideImageAltText) {
        errors.push({
          key: 'slide_missing_image_alt_text',
          options,
        })
      }
      break
    case SlideType.video:
      if (!slide.slideVideoURL) {
        errors.push({ key: 'slide_missing_video_url', options })
      }
      if (slide.mediaId) {
        if (!mediaObjects[slide.mediaId]) {
          errors.push({ key: 'global_media_missing', options })
        } else if (
          mediaObjects[slide.mediaId].data.mediaType !== MediaType.Video
        ) {
          errors.push({ key: 'global_media_wrong_type', options })
        } else if (mediaObjects[slide.mediaId].data.mediaVideoError) {
          errors.push({ key: 'global_media_video_error', options })
        } else if (
          typeof mediaObjects[slide.mediaId].data.mediaVideoDuration !==
          'number'
        ) {
          errors.push({
            key: 'global_media_missing_video_duration',
            options,
          })
        }
      } else if (typeof slide.slideVideoDuration !== 'number') {
        errors.push({ key: 'slide_missing_video_duration', options })
      }
      if (slide.slideVideoError) {
        errors.push({ key: 'slide_video_error', options })
      }
      break
    case SlideType.markdown:
      if (!slide.slideMarkdown) {
        errors.push({ key: 'slide_missing_markdown', options })
      }
      break
  }

  return errors
}

const validateAuthors = ({
  slideDeck,
  authors,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []

  if (
    slideDeck.slideDeckType !== SlideDeckType.dialog &&
    authors.length === 0
  ) {
    errors.push({ key: 'experience_must_have_author' })
  }

  authors.forEach((author) => {
    if (!author.authorImageURL) {
      errors.push({ key: 'experience_author_without_image' })
    }
  })

  return errors
}

const validateExhibits = ({
  exhibits,
  slides,
  questions,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []

  // Build a map of slideId -> slideOrder for quick lookups
  const slideOrderMap = new Map<string, number>()
  for (const slide of slides) {
    slideOrderMap.set(slide.id, slide.data.slideOrder)
  }

  // Build a map of questionId -> slideId for quick lookups
  const questionSlideMap = new Map<string, string>()
  for (const question of questions) {
    if (question.data.slideId) {
      questionSlideMap.set(question.id, question.data.slideId)
    }
  }

  exhibits.forEach((exhibit) => {
    const baseOptions = {
      exhibitId: exhibit.id,
      exhibitName: exhibit.data.exhibitName,
    }
    const hasMarkdown =
      typeof exhibit.data.exhibitMarkdown === 'string' &&
      exhibit.data.exhibitMarkdown.trim().length > 0

    if (!hasMarkdown) {
      if (!exhibit.data.exhibitAltText) {
        errors.push({
          key: 'exhibit_missing_image_alt_text',
          options: baseOptions,
        })
      }
      if (!exhibit.data.exhibitURL) {
        errors.push({
          key: 'exhibit_missing_image_url',
          options: baseOptions,
        })
      }
    }

    if (!exhibit.data.slideId) {
      errors.push({
        key: 'exhibit_missing_slide_id',
        options: baseOptions,
      })
    }

    // Validate that all input sources reference questions on slides BEFORE the exhibit's slide
    const inputSources = exhibit.data.inputSources ?? []
    if (inputSources.length > 0 && exhibit.data.slideId) {
      const exhibitSlideOrder = slideOrderMap.get(exhibit.data.slideId)

      if (exhibitSlideOrder !== undefined) {
        for (const source of inputSources) {
          const questionSlideId = questionSlideMap.get(source.questionId)
          if (questionSlideId) {
            const questionSlideOrder = slideOrderMap.get(questionSlideId)
            if (
              questionSlideOrder !== undefined &&
              questionSlideOrder >= exhibitSlideOrder
            ) {
              errors.push({
                key: 'exhibit_input_source_after_exhibit_slide',
                options: {
                  ...baseOptions,
                  variableName: source.variableName,
                  questionSlideNumber: String(questionSlideOrder + 1),
                  exhibitSlideNumber: String(exhibitSlideOrder + 1),
                },
              })
            }
          }
        }
      }
    }
  })

  return errors
}

const validateTypeSpecificRules = ({
  slideDeck,
  materials,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []

  if (slideDeck.slideDeckType === SlideDeckType.original) {
    const hasTrailer = materials.some(
      (material) =>
        material.materialType === SlideDeckMaterialType.mp4 &&
        material.materialLink &&
        material.materialName === 'Trailer'
    )
    if (!hasTrailer) {
      errors.push({ key: 'experience_must_have_trailer' })
    }

    const hasTrailerThumbnail = materials.some(
      (material) =>
        material.materialType === SlideDeckMaterialType.img &&
        material.materialLink &&
        material.materialName === 'Trailer Thumbnail'
    )
    if (!hasTrailerThumbnail) {
      errors.push({ key: 'experience_must_have_trailer_thumbnail' })
    }
  }

  return errors
}

const validateMaterials = ({
  materials,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []

  materials.forEach((material) => {
    const options = { materialName: material.materialName }

    switch (material.materialType) {
      case SlideDeckMaterialType.url:
      case SlideDeckMaterialType.pdf:
      case SlideDeckMaterialType.mp3:
      case SlideDeckMaterialType.webm:
      case SlideDeckMaterialType.featuredLarge:
      case SlideDeckMaterialType.img:
      case SlideDeckMaterialType.mp4:
        if (!material.materialLink) {
          errors.push({
            key: 'material_missing_link',
            options,
          })
        }
        break
    }
  })

  return errors
}

export const validateSlideCount = async (
  context: Pick<
    ValidationContext,
    'slideDeckId' | 'slides' | 'slideDeck' | 'firstSlideId'
  > & {
    repository: FirebaseRepository
    timeout?: number
  }
): Promise<ValidationError | null> => {
  const serverSlideCount = context.slideDeck.slideDeckSlideCount
  const {
    repository,
    slideDeckId,
    slides,
    firstSlideId,
    timeout = 5000,
  } = context

  if (slides.length === 0) return null

  const localSlideCount = slides.length
  if (localSlideCount === serverSlideCount) return null

  if (firstSlideId) {
    await touchSlide(repository, {
      slideDeckId,
      slideId: firstSlideId,
    })
  }

  const [, err] = await noTryAsync(() =>
    when(() => serverSlideCount === localSlideCount, { timeout })
  )

  return err ? { key: 'experience_slide_count_mismatch' } : null
}

export const validateEndOfSessionSlide = (
  context: ValidationContext
): ValidationError[] => {
  const errors: ValidationError[] = []
  const slides = context.slides

  const endOfSessionSlideIndex = slides.findIndex(
    (slide) => slide.data.slideType === SlideType.endOfSession
  )

  if (endOfSessionSlideIndex > -1) {
    // End of Session should be the last slide, if not - error
    if (endOfSessionSlideIndex !== slides.length - 1) {
      const endOfSessionSlide = slides[endOfSessionSlideIndex]
      errors.push({
        key: 'experience_slide_end_of_session_not_last_slide',
        options: {
          slideName: endOfSessionSlide.data.slideName,
        },
      })
    }

    // End of Session cannot be used with Processing Data or Session Results slides
    const hasProcessingDataSlide = slides.some(
      (slide) => slide.data.slideType === SlideType.processingData
    )
    const hasSessionResultsSlide = slides.some(
      (slide) => slide.data.slideType === SlideType.sessionResults
    )

    if (hasProcessingDataSlide) {
      errors.push({
        key: 'experience_slide_end_of_session_with_processing_data',
      })
    }

    if (hasSessionResultsSlide) {
      errors.push({
        key: 'experience_slide_end_of_session_with_session_results',
      })
    }
  }

  return errors
}

const validateRubrics = ({
  rubrics,
  slides,
  slideDeck,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []
  const isDemo = slideDeck.slideDeckType === SlideDeckType.demo

  // we don't need rubrics for demo slide decks, and rubric-optional types
  // surface this as a warning instead (see validateRubricsWarning)
  if (
    !isDemo &&
    rubrics.length === 0 &&
    !RUBRIC_OPTIONAL_TYPES.includes(slideDeck.slideDeckType)
  ) {
    errors.push({ key: 'experience_must_have_rubric' })
  }

  // we do need valid slide config for each rubric
  for (const rubric of rubrics) {
    const result = rubric.hasValidSlideConfiguration(slides)

    if (result.valid) continue // slide config is valid

    switch (result.reason) {
      case 'endBeforeStart':
        errors.push({
          key: 'rubric_start_slide_after_end_slide',
          options: {
            rubricId: rubric.id,
            rubricName: rubric.data.rubric,
          },
        })
        break
      case 'slideNoLongerExists':
        errors.push({
          key: 'rubric_must_have_existing_slide',
          options: {
            rubricId: rubric.id,
            rubricName: rubric.data.rubric,
          },
        })
        break
      case 'missingEnd':
        errors.push({
          key: 'rubric_has_start_without_end_slide',
          options: {
            rubricId: rubric.id,
            rubricName: rubric.data.rubric,
          },
        })
        break
    }
  }

  return errors
}

const validateRubricsWarning = ({
  rubrics,
  slideDeck,
}: ValidationContext): AdminSlideDeckPublishValidationWarning[] => {
  const warnings: AdminSlideDeckPublishValidationWarning[] = []
  const isDemo = slideDeck.slideDeckType === SlideDeckType.demo

  if (
    !isDemo &&
    rubrics.length === 0 &&
    RUBRIC_OPTIONAL_TYPES.includes(slideDeck.slideDeckType)
  ) {
    warnings.push({
      key: WarningKeys.RUBRIC_MISSING,
      description: 'This experience has no rubrics attached.',
    })
  }

  return warnings
}

const validateSoloBotDiscussionSlides = ({
  questions,
  slides,
}: ValidationContext): AdminSlideDeckPublishValidationWarning[] => {
  const { slidesWithSoloBot, slidesWithoutSoloBot } = slides.reduce(
    (acc, slide) => {
      if (slide.data.slideType !== SlideType.discussion) return acc
      const hasSoloBot = questions.some(
        (question) =>
          question.data.slideId === slide.id &&
          SlideQuestion.soloBotQuestionTypes.includes(
            question.data.questionType
          )
      )

      // only care about discussion slides
      hasSoloBot
        ? acc.slidesWithSoloBot.push(slide)
        : acc.slidesWithoutSoloBot.push(slide)

      return acc
    },
    {
      slidesWithSoloBot: [] as ValidationContext['slides'],
      slidesWithoutSoloBot: [] as ValidationContext['slides'],
    }
  )

  const warnings: AdminSlideDeckPublishValidationWarning[] = []

  if (slidesWithSoloBot.length > 0 && slidesWithoutSoloBot.length > 0) {
    // push a warning for each slide without solo bot
    slidesWithoutSoloBot.forEach((slide) => {
      warnings.push({
        key: WarningKeys.DISCUSSION_SLIDE_MISSING_SOLO_BOT,
        options: {
          slideName: slide.data.slideName,
          slideNumber: (slides.indexOf(slide) + 1).toString(),
        },
        description: `This discussion slide is missing a Solo Bot. Other discussion slides have a Solo Bot enabled.`,
      })
    })
  }

  return warnings
}

const validateEndOfSessionSlideWarning = ({
  slideDeck,
  slides,
}: ValidationContext): AdminSlideDeckPublishValidationWarning[] => {
  const warnings: AdminSlideDeckPublishValidationWarning[] = []

  if (slideDeck.slideDeckType === SlideDeckType.demo) {
    return warnings
  }

  const hasEndOfSessionSlide = slides.some(
    (slide) => slide.data.slideType === SlideType.endOfSession
  )

  if (!hasEndOfSessionSlide) {
    warnings.push({
      key: WarningKeys.MISSING_END_OF_SESSION_SLIDE,
      description:
        'This non-demo slide deck does not have an End of Session slide.',
    })
  }

  return warnings
}

const validateQuestions = async (
  repository: FirebaseRepository,
  { questions, slides }: ValidationContext
): Promise<ValidationError[]> => {
  const errors: ValidationError[] = []

  const endOfSessionSlides = slides.filter(
    (slide) => slide.data.slideType === SlideType.endOfSession
  )

  const slidesWithTalkTracksSoFar = new Set<string>()
  const slideIds = new Set(slides.map((slide) => slide.id))
  slideIds.add(PRE_MEETING_QUESTION_SLIDE_ID) // pre-meeting quiz is a special case

  const slideContextById = new Map<
    string,
    { slideName: string; slideNumber: string }
  >()
  for (const slide of slides) {
    slideContextById.set(slide.id, {
      slideName: slide.data.slideName,
      slideNumber: String(slide.data.slideOrder + 1),
    })
  }

  questions.forEach(async (questionModel) => {
    const { data } = questionModel
    const { question, slideId, groupSlideId, questionType } = data
    const questionSlideIds = [slideId, groupSlideId].filter(
      (id): id is string => Boolean(id)
    )

    const slideContext = slideId ? slideContextById.get(slideId) : undefined
    const slideName = slideContext?.slideName ?? ''
    const slideNumber = slideContext?.slideNumber

    const baseQuestionOptions: Record<string, string> = {
      // if the problem is that the question is missing, use the slide name
      // for better UX
      questionName: question || slideName,
      slideName,
      questionId: questionModel.id,
      questionType: String(questionType),
    }
    if (slideId) baseQuestionOptions.slideId = slideId
    if (slideNumber) baseQuestionOptions.slideNumber = slideNumber

    const singleValidationErrors = await validateSlideQuestion(
      { repository },
      data
    )

    for (const error of singleValidationErrors) {
      errors.push({
        key: error.key,
        options: { ...baseQuestionOptions },
      })
    }

    if (questionSlideIds.length === 0) {
      // at least one must be present
      return errors.push({
        key: 'question_invalid_slide_id',
        options: {
          questionId: questionModel.id,
          questionName: question,
        },
      })
    }

    const allMatching = questionSlideIds.every((id) => slideIds.has(id))

    const questionSlides = slides.filter((slide) =>
      questionSlideIds.includes(slide.id)
    )

    const allSlidesCompatible = questionSlides.every((slide) =>
      slide.compatibleQuestionTypes.includes(questionType)
    )

    if (!allSlidesCompatible) {
      return errors.push({
        key: 'question_slide_mismatch',
        options: { ...baseQuestionOptions },
      })
    }

    if (!allMatching) {
      // error, question is not attached to a valid slide
      return errors.push({
        key: 'question_invalid_slide_id',
        options: {
          questionId: questionModel.id,
          questionName: question,
        },
      })
    }

    // only validate attached talk tracks
    if (!slideId || questionType !== SlideQuestionType.talkTrack) return

    // error if more than one talk track on slide
    if (slidesWithTalkTracksSoFar.has(slideId))
      return errors.push({
        key: 'question_talk_track_duplicate',
        options: { ...baseQuestionOptions },
      })

    slidesWithTalkTracksSoFar.add(slideId)
  })
  // end Promise.all

  // check if end of session slide has talk track but no quiz
  endOfSessionSlides.forEach(({ id, data: { slideName, slideOrder } }) => {
    if (!slidesWithTalkTracksSoFar.has(id)) return

    const slideHasQuiz = questions.some(
      (q) => q.data.slideId === id && q.isQuiz
    )

    if (!slideHasQuiz)
      errors.push({
        key: 'question_talk_track_end_of_session_no_quiz',
        options: {
          slideId: id,
          slideName,
          slideNumber: String(slideOrder + 1),
        },
      })
  })

  return errors
}

const validateSoloBot = ({
  questions,
  slides,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []

  for (const slide of slides) {
    const slideQuestions = questions.filter(
      (question) => question.data.slideId === slide.id
    )

    const soloBot = slideQuestions.find(({ questionType }) =>
      SlideQuestion.soloBotQuestionTypes.includes(
        questionType as SlideQuestionType
      )
    )
    const moderationTasks = slideQuestions.filter(
      (question) =>
        question.data.questionType === SlideQuestionType.moderationTask
    )

    if (soloBot && moderationTasks.length === 0) {
      errors.push({
        key: 'solo_bot_missing_moderation_task',
        options: {
          slideId: slide.id,
          slideName: slide.data.slideName,
          slideNumber: String(slide.data.slideOrder + 1),
        },
      })
    }
  }

  return errors
}

const validateRoles = ({
  roles,
  rubrics,
  materials,
  exhibits,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []

  // validate every role is used at least once
  for (const role of roles) {
    const roleName = role.data.roleName

    const roleIsAssignedToItem = [...rubrics, ...materials, ...exhibits].some(
      (item) => {
        const data = 'data' in item ? item.data : item
        return data.assignmentRoles?.includes(role.id)
      }
    )

    if (!roleIsAssignedToItem) {
      errors.push({
        key: 'role_not_assigned_to_item',
        options: {
          roleId: role.id,
          roleName,
        },
      })
    }
  }

  const maxGroupSize = RoomState.maxAllowedUsers

  // get the min sum of all roles
  const totalMin = roles.reduce((sum, r) => sum + r.data.roleMin, 0)
  if (totalMin > maxGroupSize) {
    errors.push({
      key: 'roles_minimum_sum_invalid',
      options: {
        maxGroupSize: maxGroupSize.toString(),
      },
    })
  }

  // check all exhibits, materials, rubrics have valid role assignments
  rubrics.forEach((rubric) => {
    if (!rubric.data.assignmentRoles || !rubric.data.assignmentRoles.length)
      return
    const roleExists = roles.some(
      (role) => role.id === rubric.data.assignmentRoles?.[0]
    )
    if (!roleExists) {
      errors.push({
        key: 'rubric_role_invalid',
        options: {
          rubricId: rubric.id,
          rubricName: rubric.data.rubric,
        },
      })
    }
  })

  exhibits.forEach((exhibit) => {
    if (!exhibit.data.assignmentRoles || !exhibit.data.assignmentRoles.length)
      return
    const roleExists = roles.some(
      (role) => role.id === exhibit.data.assignmentRoles?.[0]
    )
    if (!roleExists) {
      errors.push({
        key: 'exhibit_role_invalid',
        options: {
          exhibitId: exhibit.id,
          exhibitName: exhibit.data.exhibitName,
        },
      })
    }
  })
  materials.forEach((material) => {
    if (!material.assignmentRoles || !material.assignmentRoles.length) return
    const roleExists = roles.some(
      (role) => role.id === material.assignmentRoles?.[0]
    )
    if (!roleExists) {
      errors.push({
        key: 'material_role_invalid',
        options: {
          materialName: material.materialName,
        },
      })
    }
  })
  return errors
}

export const validatePrice = (
  context: ValidationContext
): ValidationError[] => {
  const errors: ValidationError[] = []

  const isFreeType = checkIsFreeType(context.slideDeck.slideDeckType)
  const price = context.slideDeck.slideDeckPrice

  if (!isFreeType && price < MIN_PRICE) {
    errors.push({
      key: 'experience_price_too_low',
      options: {
        price: MIN_PRICE.toString(),
      },
    })
  }

  if (isFreeType && price > 0) {
    errors.push({
      key: 'experience_price_too_high',
    })
  }

  return errors
}
